<template>
    <div id="form-wrapper">
        <form
            id="responseForm"
            ref="form"
            class="form"
            autocomplete="off"
            novalidate="novalidate"
            method="post"
            :action="actionUrl"
            data-tf-element-role="offer"
            @submit.prevent
        >
            <Transition
                v-for="(step, index) in content.steps"
                :key="index"
                name="form-step"
            >
                <zoe-form-step
                    v-if="store.state.form.state === 'form'"
                    v-show="store.state.form.attributes.step === (index + 1)"
                    :key="index"
                    :show="store.state.form.attributes.step === (index + 1)"
                    :index="index"
                    :elements="step"
                    :start-from="startFrom(index)"
                    @update-height="updateHeight"
                    @pre-validate-step="preValidateStep"
                />
            </Transition>

            <Transition name="form-step">
                <zoe-organism-sms-verification
                    v-if="store.state.form.state === 'smsVerification'"
                    :content="content.smsVerification"
                    :hash="store.state.form.smsVerification.hash"
                    :phone="store.state.form.smsVerification.phone"
                />
            </Transition>

            <zoe-organism-trusted-form v-if="store.state.page.needsTrustedForm && store.state.form.fields.length > 1" />
        </form>

        <form
            v-if="store.state.page.isLanding"
            id="thankyouForm"
            ref="thankyouForm"
            method="post"
            :action="thankyouPageUrl"
        >
            <input
                v-for="input in store.getters['form/getFormData']()"
                :key="input.name"
                type="hidden"
                :name="input.name"
                :value="input.value"
            >
            <input
                v-for="[key, data] of Object.entries(store.state.quiz.extradata)"
                :key="key"
                type="hidden"
                :name="key"
                :value="data.value"
            >
        </form>
    </div>
</template>

<script setup>
import { computed, nextTick, onMounted, ref } from 'vue';
import { usePage } from '@inertiajs/vue3';
import { useStore } from 'vuex';
import { isDesktopListener } from '../../../../resources/frontend/js/mixins/is-desktop-listener';
import { preValidateStep } from '@form/utils/pre-validate';

import ZoeFormStep from '@form/zoe-form-step.vue';
import ZoeOrganismSmsVerification from '@organisms/sms-verification/zoe-organism-sms-verification.vue';
import ZoeOrganismTrustedForm from '@organisms/trusted-form/v1.0.0/zoe-organism-trusted-form.vue';

const store = useStore();
const page = usePage();
const props = defineProps({
    content: {
        type: Object,
        required: true,
    },
});

const form = ref(null);
const lastHeight = ref(0);

const actionUrl = computed(() => {
    return window.location.pathname.replace(`/${page.props.localizedThankyou}`, '') + window.location.search;
});

const thankyouPageUrl = computed(() => {
    const url = new URL(window.location.href);
    if (window.location.pathname.includes(page.props.localizedThankyou)) {
        return window.location.pathname + window.location.search;
    }

    const searchParams = new URLSearchParams(url.search);
    searchParams.delete('current_step');
    searchParams.delete('current_question');
    searchParams.delete('form_step');

    return window.location.pathname + '/' + page.props.localizedThankyou + '?' + searchParams.toString();
});

const saveProperties = () => {
    store.commit('component/setOrganismValue', {
        component: 'form',
        object: { height: form.value.clientHeight },
    });
};

const startFrom = index => {
    let number = 0;

    for (let i = index - 1; i >= 0; i--) {
        number += props.content.steps[i].length;
    }

    return number;
};

const updateHeight = height => {
    const prevHeight = lastHeight.value + 'px';
    const nextHeight = height + 'px';

    if (lastHeight.value > 0) {
        form.value.style.height = prevHeight;

        setTimeout(() => {
            form.value.style.height = nextHeight;
        }, 50);

        setTimeout(() => {
            form.value.style.height = '';
        }, 450);
    }

    lastHeight.value = height;
};

onMounted(() => {
    store.commit('form/setAttribute', { key: 'amountOfSteps', value: props.content.steps.length });
    isDesktopListener(4, saveProperties);

    nextTick(() => {
        saveProperties();
    });

    if (store.state.page.isPreview && props.content.smsVerification?.preview.value) {
        store.state.form.state = 'smsVerification';
    }
});
</script>

<style lang="scss">
#form-wrapper .form {
    transition: height .3s ease;
}

.form-step-leave-active {
    transition: all .3s ease;
}

.form-step-enter-active {
    transition: all .3s ease .3s;
}

.form-step-leave-to,
.form-step-enter-from {
    position: absolute !important;
    top: 0;
    opacity: 0;
}
</style>
